import React, { useState, useEffect } from "react";
import Input from "../../components/Input";
import { SaveCancel } from "../../components/PrimaryButton";
import { securePut, api } from "../../config/api";
import { getResponseData } from '../../config/api';
import { getJwkKeystore } from '../../utils/encryption';
import { useTranslation } from "react-i18next";
import { formatAmount } from "../../utils/format";
import SnackbarNotification from '../../components/SnackbarNotification';

import DeleteIcon from "../../icons/trashR.svg";
import PlusIcon from "../../icons/plus.svg";

const cleanDecimal = (raw) => {
  if (raw == null) return "";
  let v = String(raw)
    .replace(/[^0-9,.]/g, "")
    .replace(/,/g, ".")
    .replace(/\.(?=.*\.)/g, "") 
    .replace(/^\./, "0.");     
  const m = v.match(/^(\d+)(?:\.(\d{0,2}))?$/);
  if (!m) {
    if (v === "" || v === "0.") return v;
    const [intPart, decPart = ""] = v.split(".");
    return decPart ? `${intPart}.${decPart.slice(0, 2)}` : intPart;
  }
  return v;
};

const ProductForm = ({
  title,
  initialValues = {},
  baseFields,
  fileFields,
  onSubmit,
  onSuccess,
  onCancel,
  apiUrl,
}) => {
  const [formValues, setFormValues] = useState(initialValues);
  const [files, setFiles] = useState({});
  const [fileNames, setFileNames] = useState({});
  const [previews, setPreviews] = useState({});
  const [uploadedImage,] = useState(initialValues.image || "");
  const [imageChanged, setImageChanged] = useState(false);
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const tva = parseFloat(process.env.REACT_APP_TVA);

  console.log("initialValues in product", initialValues);
  useEffect(() => {
    if (initialValues) {
      const familyIdValue = initialValues.family?.id || initialValues.familyId || "";
      const brandIdValue = initialValues.brand?.id || initialValues.brandId || "";
      // Get familyType from multiple sources: direct property, family object, or empty string
      const familyTypeValue = initialValues.familyType || initialValues.family?.familyType || "";
      console.log("Setting formValues - familyId:", familyIdValue, "brandId:", brandIdValue, "familyType:", familyTypeValue);
      setFormValues({
        ...initialValues,
        brandId: brandIdValue,
        familyId: familyIdValue,
        familyType: familyTypeValue, // Preserve familyType for visibility logic
      });
    }
  }, [initialValues]);

  useEffect(() => {
    if (initialValues.image) {
      const match = initialValues.image.match(/grayfox\/([^?]+)/);
      if (match) {
        setFileNames((prev) => ({
          ...prev,
          logo: match[1],
        }));
      }
    }
  }, [initialValues.image]);


const handleInputChange = (name, value) => {
  const rate = Number.isFinite(tva) ? tva : 0;
  const v = cleanDecimal(value);

  if (name === "priceHT") {
    setFormValues(prev => ({ ...prev, priceHT: v }));
    const ht = parseFloat(v);
    if (!isNaN(ht)) {
      const ttc = (ht * (1 + rate)).toFixed(2);
      setFormValues(prev => ({ ...prev, priceTTC: ttc }));
    } else {
      setFormValues(prev => ({ ...prev, priceTTC: "" }));
    }
    return;
  }

  if (name === "priceTTC") {
    setFormValues(prev => ({ ...prev, priceTTC: v }));
    const ttc = parseFloat(v);
    if (!isNaN(ttc)) {
      const ht = (ttc / (1 + rate)).toFixed(2);
      setFormValues(prev => ({ ...prev, priceHT: ht }));
    } else {
      setFormValues(prev => ({ ...prev, priceHT: "" }));
    }
    return;
  }

  // When familyId changes, update familyType from dropdown options
  if (name === "familyId") {
    const familyField = baseFields.find(f => f.name === "familyId");
    const selectedFamilyOption = familyField?.options?.find(opt => opt.value === value);
    const newFamilyType = selectedFamilyOption?.familyType || "";
    setFormValues(prev => ({ 
      ...prev, 
      [name]: value,
      familyType: newFamilyType, // Update familyType when familyId changes
    }));
    return;
  }

  setFormValues(prev => ({ ...prev, [name]: value }));
};


  const handleFileChange = (e) => {
    const { name } = e.target;
    const file = e.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setPreviews(prev => ({ ...prev, [name]: previewUrl }));
      setFiles((prev) => ({ ...prev, [name]: file }));
      setFileNames((prev) => ({ ...prev, [name]: file.name }));
      setImageChanged(true);
    }
  };

  const removeFile = (fieldName) => {
    URL.revokeObjectURL(previews[fieldName]);
    setPreviews(prev => {
      const updated = { ...prev };
      delete updated[fieldName];
      return updated;
    });
    setFiles((prev) => {
      const updatedFiles = { ...prev };
      delete updatedFiles[fieldName];
      return updatedFiles;
    });
    setFileNames((prev) => {
      const updatedNames = { ...prev };
      delete updatedNames[fieldName];
      return updatedNames;
    });
    setImageChanged(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Determine familyType to filter out reference field if SERVICE and for brandId logic
    let familyType = initialValues?.familyType || 
                    initialValues?.family?.familyType || 
                    formValues?.familyType || "";
    
    // If no familyType but we have a selected familyId, get the familyType from dropdown options
    if (!familyType && formValues?.familyId) {
      const familyField = baseFields.find(f => f.name === "familyId");
      const selectedFamilyOption = familyField?.options?.find(opt => opt.value === formValues.familyId);
      familyType = selectedFamilyOption?.familyType || "";
    }
    
    const requiredFields = [...baseFields, ...fileFields];
    // Filter out reference field if familyType is SERVICE
    let missing = requiredFields.filter(f => {
      // Skip reference field validation if familyType is SERVICE
      if (f.name === "reference" && familyType === "SERVICE") {
        return false;
      }
      return f.required && !formValues[f.name];
    });
    
    if (missing.length > 0) {
      showSnackbar(`Veuillez remplir les champs obligatoires : ${missing.map(f => f.label).join(', ')}`, 'error');
      return;
    }
    let finalImageName = "";
    let includeImage = false;

    if (uploadedImage) {
      const match = uploadedImage.match(/grayfox\/([^?]+)/); 
      if (match) {
        finalImageName = match[1];
      }
    }

    if (imageChanged && files.logo) {
      const formData = new FormData();
      formData.append("file", files.logo);

      try {
        const fileUploadResponse = await api.post(
          "/attachments/images/add",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: localStorage.getItem("token"),
            },
          }
        );

        const key = await getJwkKeystore();
        const decryptedResponse = await getResponseData(fileUploadResponse, key);

        if (decryptedResponse && decryptedResponse.fileName) {
          finalImageName = decryptedResponse.fileName;
          includeImage = true;
        } else {
          throw new Error("File name not found in the decrypted response");
        }
      } catch (error) {
        console.error("Error uploading file:", error.response?.data || error);
        setSnackbar({
          open: true,
          message: t(`errors.generic`),
          severity: "error",
        });
        return;
      }
    }
    
    // Hide brandId when familyType is "SERVICE" or "ASSET", and set brandId to DRIVNEST brand id
    const shouldHideBrand = familyType === "SERVICE" || familyType === "ASSET";
    let brandIdValue = formValues.brandId;
    
    // If brandId should be hidden (SERVICE or ASSET) and brandId is not set, find DRIVNEST brand id
    if (shouldHideBrand && !brandIdValue) {
      // Find the brandId field from baseFields to get brands options
      const brandField = baseFields?.find(field => field.name === "brandId");
      const brandsOptions = brandField?.options || [];
      // Find the brand with label === "DRIVNEST"
      const drivnestBrand = brandsOptions.find(brand => brand.label === "DRIVNEST");
      brandIdValue = drivnestBrand ? drivnestBrand.value : 1; // Fallback to 1 if DRIVNEST not found
    }
    
    // If familyType is PRODUCT and brandId is not set, keep it as is (will be validated as required)

    const formPayload = {
      ...(initialValues?.id && { id: initialValues.id }),
      name: formValues.name || "",
      // Only include reference if familyType is not SERVICE
      ...(familyType !== "SERVICE" && { reference: formValues.reference || "" }),
      priceHT: parseFloat(formValues.priceHT),
      priceTTC: Number(formValues.priceTTC),
      discount: Number(formValues.discount) || null,
      salesDiscountLimit: Number(formValues.salesDiscountLimit) || null,
      brandId: Number(brandIdValue),
      familyId: Number(formValues.familyId),
      tva: Number(formValues.tva),
    };

    if (includeImage) {
      formPayload.image = finalImageName;
    } else if (!imageChanged && finalImageName) {
      formPayload.image = finalImageName;
    }

    console.log("Final payload prepared for submission:", formPayload);

    try {
      setSubmitting(true);
      const response = await securePut(apiUrl, formPayload, showSnackbar);
      console.log("Form submitted successfully. Response data:", response.data);

      onSubmit(response.data);
      // Pass familyType to onSuccess so it can determine the correct redirect URL
      onSuccess(familyType);

    } catch (error) {
      console.error("Error during form submission:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[1050]">
      <div className="bg-white rounded-[12px] shadow-lg w-full max-w-screen-lg max-h-[90vh] flex flex-col">
        <div className="p-6 pb-0 sticky top-0 z-10">
          <h2 className="text-[32px] text-[#38485C] font-semibold">{title}</h2>
        </div>
        <div className="overflow-y-auto px-8 py-6 space-y-6 flex-1">

          <form onSubmit={handleSubmit} className="space-y-6">

          {/* Base Fields */}
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                {baseFields
                  .filter((field) => {
                    // Get familyType from multiple sources
                    let familyType = initialValues?.familyType || 
                                    initialValues?.family?.familyType || 
                                    formValues?.familyType || "";
                    
                    // If no familyType but we have a selected familyId, get it from dropdown options
                    if (!familyType && formValues?.familyId) {
                      const familyField = baseFields.find(f => f.name === "familyId");
                      const selectedFamilyOption = familyField?.options?.find(opt => opt.value === formValues.familyId);
                      familyType = selectedFamilyOption?.familyType || "";
                    }
                    
                    // Hide brand field if familyType is "SERVICE" or "ASSET"
                    if (field.name === "brandId") {
                      if (familyType === "SERVICE" || familyType === "ASSET") {
                        return false; // Hide brand field
                      }
                    }
                    
                    // Hide reference field if familyType is "SERVICE"
                    if (field.name === "reference") {
                      if (familyType === "SERVICE") {
                        return false; // Hide reference field
                      }
                    }
                    
                    return true;
                  })
                  .map((field, index) => (
                  <Input
                    key={index}
                    label={field.label}
                    name={field.name}
                    type={field.type || "text"}
                      options={field.options || []}
                    value={formValues[field.name] || ""}
                    required={field.required}
                    onChange={(value) => handleInputChange(field.name, value)}
                  />
                ))}
              </div>
            </div>

            {/* File Field */}
            {fileFields && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4">
                  {fileFields.map((field, index) => {
                    const hasFile = Boolean(fileNames[field.name]);
                    const existingImage = initialValues.image;
                    const previewImage = previews[field.name];

                    return (
                      <div key={index} className="space-y-2">
                        <label
                          htmlFor={`fileInput-${field.name}`}
                          className={` border-2 border-dashed h-[93px] rounded-md p-4 block cursor-pointer transition-colors
                                ${hasFile ? 'border-gray-300' : 'border-gray-300'} `}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => {
                            e.preventDefault();
                            const droppedFile = e.dataTransfer.files?.[0];
                            if (droppedFile) {
                              const validTypes = field.accept.split(',').map(type => type.trim());
                              const isValid = validTypes.some(type => {
                                if (type.endsWith('/*')) {
                                  const [mainType] = type.split('/');
                                  return droppedFile.type.startsWith(`${mainType}/`);
                                }
                                return droppedFile.type === type;
                              });

                              if (isValid) {
                                handleFileChange({
                                  target: {
                                    name: field.name,
                                    files: [droppedFile]
                                  }
                                });
                              } else {
                                showSnackbar(t('errors.invalidFileType', { types: field.accept }), 'error');
                              }
                            }
                          }}
                        >
                          <input
                            id={`fileInput-${field.name}`}
                            type="file"
                            name={field.name}
                            accept={field.accept}
                            className="hidden"
                            onChange={(e) => {
                              const selectedFile = e.target.files?.[0];
                              if (selectedFile) {
                                if (
                                  field.accept.includes(selectedFile.type) ||
                                  (field.accept === "image/*" && selectedFile.type.startsWith("image/"))
                                ) {
                                  handleFileChange(e);
                                }
                              }
                            }} />
                          <div className="flex items-center justify-between gap-3 h-full min-h-[40px]">
                            {hasFile ? (
                              <>
                                <div className="flex items-center ml-[40px] min-w-0 flex-1 h-full">
                                  {(previewImage || existingImage) && (
                                    <div className="relative w-20 h-20 shrink-0">
                                      <img
                                        src={previewImage || existingImage}
                                        alt="Preview"
                                        className="w-full h-full object-contain rounded-md"
                                      />
                                    </div>
                                  )}
                                  <span className="text-[14px] font-semibold ml-[20px] text-[#38475D] truncate">
                                    {fileNames[field.name]}
                                  </span>
                                </div>

                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    removeFile(field.name);
                                  }}
                                className="w-8 h-8 p-1 -m-1 mr-[36px] flex items-center justify-center bg-[#F9FAFC] rounded-[12px] border border-[#EF466F4D]"
                                >
                                  <img src={DeleteIcon} alt="Delete" className="w-5 h-5" />
                                </button>
                              </>
                            ) : (
                              <div className="flex items-center justify-center w-full gap-4 text-center">
                                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                                <div className="text-left">
                                  <p className="text-sm text-gray-500">{field.placeholderText1}</p>
                                  <p className="text-sm font-bold text-primary">{field.placeholderText2}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          </form>
        </div>
        {/* Save/Cancel Buttons */}
        <div className="p-6 pt-0  sticky bottom-0z-10 flex justify-end space-x-4">
          <SaveCancel onCancelClick={onCancel} onSaveClick={handleSubmit} loading={submitting} />
        </div>

      </div>
      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default ProductForm;
